import {
    DataTable,
    AuthContext,
    DataTablePresenter,
    IAuthContextProps, IFilterSchema,
    ITableColumn,
    ITableFetchOptions, ListProviderRefundRequestsRequest, PatientBadge,
    ProviderCollectionRefundRequest, ProviderThinPatient,
    renderAmount,
    renderDateTime, ProviderClaim, FilterMethod, ToastContext
} from "@enter/core";
import {DataTableCard, PageHeader, PageLayout, PageLayoutRow, ProviderMessagingContext} from 'components';
import { DialogContext, IDialogContextProps } from 'services';
import * as S from './overpayments.styles';
import {IInjectedRootStore, IObservedRootStore, parseSortBy, RootStore} from "stores";
import {inject, observer} from "mobx-react";
import React, { useContext } from 'react';
import {NotesDialog} from "../../../components/notes-dialog/notes-dialog";

interface IOverpaymentsProps extends IInjectedRootStore {
    authContext: IAuthContextProps;
    dialogContext: IDialogContextProps;
}

interface IOverpaymentsState {
    loading: boolean;
    options: ITableFetchOptions | undefined;
}

const filterSchemas: IFilterSchema<keyof ListProviderRefundRequestsRequest>[] = [
    {
        label: 'External Patient Id',
        filterId: 'filterExternalPatientId',
        columnId: 'patient',
        filterType: 'String',
    }
];


const columnsBase: ITableColumn<ProviderCollectionRefundRequest>[] = [
    {
        id: 'Id',
        Header: 'Id',
        accessor: 'id',
        disableSortBy: true,
        Cell: ({ cell }) => <S.Cell>{cell.value}</S.Cell>,
    },
    {
        id: 'RefundAmount',
        Header: 'Refund Amount',
        accessor: 'refundAmount',
        disableSortBy: true,
        Cell: ({ cell }) => <S.Cell>{renderAmount(cell.value)}</S.Cell>,
    },
    {
        id: 'Patient',
        Header: 'Patient',
        accessor: 'patient',
        disableSortBy: true,
        Cell: ({ cell }) => {
            const getAccountId = (): string | undefined => {
                const pathParts = window.location.pathname.split('/').filter(Boolean);
                return pathParts.length > 0 && pathParts[0].length === 36 ? pathParts[0] : undefined;
            };
            const accountId = getAccountId();
            const patient = cell.value as ProviderThinPatient;
            const path = accountId ? `/${accountId}/patients/${patient?.patientId}/statement` : `/patients/${patient?.patientId}/statement`;
            return <S.Cell>{
                cell.value ?
                <PatientBadge
                    id={patient.patientId}
                    first={patient.first}
                    last={patient.last}
                    suffix={patient.suffix}
                    tooltip="View Patient Statement"
                    path={path} /> : <></>
            }</S.Cell>;
        },
    },
    {
        id: 'CreateDate',
        Header: 'Created',
        accessor: 'created',
        disableSortBy: true,
        Cell: ({ cell }) => <S.Cell>{renderDateTime(cell.value)}</S.Cell>,
    },
];


class OverpaymentsPendingPage extends React.Component<IOverpaymentsProps, IOverpaymentsState> {
    private presenter: DataTablePresenter<ProviderCollectionRefundRequest>;

    private columns: ITableColumn<ProviderCollectionRefundRequest>[] = [];

    constructor(props: IOverpaymentsProps) {
        super(props);
        this.state = {
            loading: false,
            options: undefined,
        };
        const rootStore: RootStore = props.rootStore;
        const columns: ITableColumn<ProviderCollectionRefundRequest>[] = [
            ...columnsBase,
            {
                id: 'Action',
                Header: '',
                disableSortBy: true,
                Cell: ({ row }): React.ReactElement => {
                    const messagingContext = useContext(ProviderMessagingContext);
                    const dialogContext = useContext(DialogContext);
                    const toastContext = useContext(ToastContext);

                    const collectionRefundRequest: ProviderCollectionRefundRequest|undefined = rootStore.overpaymentStore.findCollectionRefundRequest(row.original.id ? row.original.id : '');

                    const handleApprove = async () => {
                        const handleClose = (): void => {
                            dialogContext.hideDialog();
                        };

                        const handleSubmit = async (notes?: string): Promise<string|undefined> => {
                            console.log("Notes were: ", notes);
                            dialogContext.hideDialog();
                            if(collectionRefundRequest && collectionRefundRequest.id) {
                                await rootStore.overpaymentStore.approveRequest(collectionRefundRequest.id, notes);
                                toastContext.notify?.('Refund has been approved', 'right');
                                window.location.reload(); //For now, just reload the screen
                            }
                            return 'Refund Approved';
                        }

                        dialogContext.showDialog({
                            props: {
                                onClose: handleClose,
                                disableBackdropClick: true,
                                disableEscapeKeyDown: true,
                                renderTitle: 'Approve Refund',
                            },
                            Component: <NotesDialog
                                onClose={handleClose}
                                onSubmit={handleSubmit}
                            />,
                        });
                    };

                    const handleMessage = () => {

                        const patient = collectionRefundRequest?.patient;
                        const refundAmount = collectionRefundRequest?.refundAmount;
                        const guarantor = collectionRefundRequest?.businessGuarantor;

                        let message = '';

                        if(patient) {
                            message = "Patient: " + patient.first + " " + patient.last + " - (" + patient.dob + ")\n"
                            message+= "Patient external id: " + patient.externalId + "\n";
                        } else if(guarantor) {
                            message = "Guarantor: " + guarantor.organization + " ("  + guarantor.externalOrganizationId + ")\n"
                        }

                        if(refundAmount) {
                            message+= "Refund: " + refundAmount.amount + "\n";
                        }

                        message+= "\nQuestion:\n";

                        messagingContext.openPanel?.({
                            isComposeOpen: true,
                            defaultSubject: 'Question About Refund',
                            defaultBody: message
                        });
                    };

                    return (
                        <S.ActionButton
                            id={row.original.id as string}
                            actions={[
                                {
                                    label: 'Approve',
                                    onClick: handleApprove,
                                },
                                {
                                    label: 'Create a Message',
                                    onClick: handleMessage,
                                },
                            ]}
                        />
                    );
                },
            },
        ];

        this.presenter = new DataTablePresenter<ProviderCollectionRefundRequest>({
            name: 'overpayments',
            data: [],
            columns,
            filterSchemas,
        });

        this.columns = columns;
    }


    public componentDidMount(): void {
        document.title = 'ENTER Platform - Overpayments';
    }
    public componentWillUnmount(): void {
        this.authContext.cancelGroup('overpayments');
    }

    public render() {
        return (
            <S.SubPageLayout>
                <DataTableCard size="large">
                    <DataTable presenter={this.presenter} isLoading={this.state.loading} onFetch={this.onFetch} />
                </DataTableCard>
            </S.SubPageLayout>
        );
    }



    private get authContext(): IAuthContextProps {
        return this.props.authContext;
    }

    private get dialogContext(): IDialogContextProps {
        return this.props.dialogContext;
    }


    private onFetch = async (fetchOptions: ITableFetchOptions): Promise<void> => {
        try {
            this.setState({
                options: fetchOptions,
                loading: true,
            });
            this.authContext.cancelGroup('overpayments');
            this.authContext.setGroup('overpayments');

            const { pageIndex, pageSize, sortBy, validFilters } = fetchOptions;
            const sortAndOrder = parseSortBy(sortBy, this.columns);
            const listing = await this.props.rootStore.overpaymentStore.loadOverpaymentRefundListing({
                page: pageIndex,
                pageSize,
                ...sortAndOrder,
                filters: [
                    {filterId: 'filterApproveDate', filterMethod: FilterMethod.EMPTY, filterType: 'String', value: ''},
                    {filterId: 'filterDenyDate', filterMethod: FilterMethod.EMPTY, filterType: 'String', value: ''}, ...validFilters],
            });
            this.presenter.onDataUpdate(listing.items || []);
            this.presenter.onDataTotalChange(listing.total || 0);
            this.setState({
                loading: false,
            });
        } catch (err) {
            this.dialogContext.showError(err);
        }
    };

}


export const OverpaymentsPending: React.FC<IObservedRootStore> = inject('rootStore')(
    observer((props: IObservedRootStore) => {
        const { rootStore } = props;
        if (!rootStore) {
            return <></>;
        }
        const authContext = useContext(AuthContext);
        const dialogContext = useContext(DialogContext);
        return <OverpaymentsPendingPage authContext={authContext} dialogContext={dialogContext} rootStore={rootStore} />;
    }),
);